import React, { type ReactElement, useState } from 'react';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import { faRecycle } from '@fortawesome/free-solid-svg-icons';
import Card from 'atom-components/card';
import Icon from 'atom-components/icon';
import TableActions from 'atom-components/table-actions-lite';
import TooltipItem from 'atom-components/tooltip-item';
import { friendlyMarkerName } from 'helpers/domain-specific/marker.helpers';
import ConfirmDeleteMarkModal from 'pages/marking/components/confirm-delete-mark-modal';
import { markResponseHref } from 'pages/marking/helpers/presentation.helpers';
import MarkPolicy from 'policies/mark.policy';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import {
  type ManageMarkingMark,
  MarkType
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { type TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { usePageAccess } from 'services/providers/page-access.provider';
import DeallocationResultsMarkerBadge from './deallocation-results-marker-badge';
import { allocatedMarkerTooltip } from './deallocation-results.helpers';
import MarkStatusWarningBadge from './mark-status-warning-badge';
import styles from './mark.module.scss';

type Props = {
  responseId: number;
  mark: ManageMarkingMark;
  allocatedMarkerId: number;
  activeMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  organisationUser: BaseCustomerUser | undefined;
  onDeleteMark: () => void;
};

export default function Mark({
  responseId,
  mark,
  allocatedMarkerId,
  activeMarker,
  organisationUser,
  onDeleteMark
}: Props): ReactElement {
  const [isConfirmDeleteMarkModalOpen, setIsConfirmDeleteMarkModalOpen] = useState<boolean>(false);
  const allocatedMarkerIsMarker = mark?.activeMarker?.id === allocatedMarkerId;

  const markerBadgeTooltip = allocatedMarkerTooltip(
    allocatedMarkerIsMarker,
    friendlyMarkerName(organisationUser),
    true
  );

  const { authorisableUser } = usePageAccess();
  // Note: Response markers are not relevant to the deletion scenario, but if that changes we'll
  // need to add them to authorisable mark.
  const authorisableMark = {
    id: mark.id,
    type: mark.type,
    status: mark.status,
    responseMarkers: []
  };

  return (
    <div className={styles.mark}>
      <ConfirmDeleteMarkModal
        isOpen={isConfirmDeleteMarkModalOpen}
        onClose={(): void => setIsConfirmDeleteMarkModalOpen(false)}
        onConfirm={onDeleteMark}
        markId={mark.id}
      />
      <Card.Heading level={3}>
        <TooltipItem tooltip={markerBadgeTooltip}>
          <span className={styles.checkOrXMarkIcon}>
            <Icon icon={allocatedMarkerIsMarker ? 'xmark' : 'check'} />
          </span>
        </TooltipItem>
        Mark ID #{mark.id}
      </Card.Heading>
      <div className={styles.blocks}>
        <div className={styles.block}>
          <MarkStatusWarningBadge>{mark.status}</MarkStatusWarningBadge>
        </div>
        <div className={styles.block}>
          <DeallocationResultsMarkerBadge
            isMark
            allocatedMarkerIsMarker
            activeMarker={activeMarker}
            organisationUser={organisationUser}
            autoscored={mark.type === MarkType.Autoscored}
          />
        </div>
        <div className={styles.block}>
          {/* Although we're not displaying a table row, the TableActions component fits here. */}
          <TableActions>
            <TableActions.Link
              tooltip="Mark Response (Edit Mark)"
              href={markResponseHref(responseId, mark.id)}
              icon={faPenToSquare}
              openInNewTab
            />
            <TableActions.Button
              tooltip="Delete Mark"
              onClick={(): void => setIsConfirmDeleteMarkModalOpen(true)}
              icon={faRecycle}
              disabled={!MarkPolicy.delete(authorisableUser, authorisableMark)}
              isDangerous
            />
          </TableActions>
        </div>
      </div>
    </div>
  );
}
